<template>
    <div>
        <div class="grid grid-cols-12 gap-6">
            <div class="hidden md:col-span-1 md:block" />
            <div class="col-span-12 text-left md:col-span-10">
                <h2>Prameny – seznamy ulic</h2>
                <div class="mt-5 mb-5">
                    <img
                        src="../../public/img/articles/prameny_seznamy_ulic.webp"
                        alt=""
                        class="mb-1 aspect-auto w-full object-cover"
                    />
                    <span class="source"
                        >Seznam všech domů, ulic a náměstí královského hlavního
                        a rezidenčního města Prahy podle nového a starého
                        číslování z roku 1805, s. 8–9. / Zdroj: Verzeichniß
                        aller Häuser, Straßen und Plätze der königlichen Haupt-
                        und Residenzstadt Prag, nach der neuen und alten
                        Numerirung; mit Angabe der Hausbesitzer und Schilde und
                        einer vergleichenden Übersicht der alten und neuen
                        Numern, Praha 1805.</span
                    >
                </div>
                <p class="mb-5 font-bold">
                    Jak se měnily názvy pražských ulic? <br />
                    Přehled vývoje seznamů pražských ulic od 18. století po
                    současnost ukazuje, jak se měnily nejen samotné názvy, ale i
                    přístup k jejich evidenci. Od rukopisných soupisů přes
                    tištěné rejstříky až po digitální registr RÚIAN – článek
                    nabízí stručný přehled, zejména pro milovníky historie a
                    urbanisty, jak se Praha vyvíjela v mapách i v názvosloví.
                </p>

                <p class="mb-5">
                    Pro období po zavedení úředních názvů ulic ve spojených
                    pražských městech koncem 18. století jsou důležitými
                    opěrnými body popisy města a více či méně úřední seznamy
                    domů, ulic a náměstí.
                </p>

                <p class="font-bold underline">
                    18. století – počátky úřední evidence
                </p>
                <p class="mb-5">
                    <span class="font-bold">1787</span>
                    – Úplný popis královského hlavního a rezidenčního města
                    Prahy od nejstarších až po současné časy (Jan Ferdinand
                    Opitz, Vollständige Beschreibung der königlichen Haupt- und
                    Residenzstadt Prag, von den ältesten bis auf die ietzige
                    Zeiten, Praha – Vídeň 1787).
                </p>
                <p class="mb-5">
                    <span class="font-bold">1788/1789</span>
                    – Rukopisný soupis: Jména všech náměstí, ulic, domů včetně
                    jejich vlastníků, znamení a čísel královského hlavního města
                    Prahy (Namen aller Plätze, Strassen, Häußer, nebst ihren
                    Inhabern, Schildern und Nummern der königliche Hauptstadt
                    Prag, Archiv hl. m. Prahy, Sbírka úředních knih a rukopisů,
                    č. 8719).
                </p>
                <p class="mb-5">
                    <span class="font-bold">1794–1797</span>
                    – Popis královského hlavního a rezidenčního města Prahy
                    (Jaroslav Schaller, Beschreibung der königlichen Haupt- und
                    Residenzstadt Prag I–IV, Prag 1794–1797). České názvy ulic
                    patrně nejsou vždy autentické a užívané, jde spíše o snahu o
                    jejich překlad.
                </p>

                <p class="font-bold underline">
                    19. století – snahy o zachycení změn
                </p>
                <p class="mb-5">
                    <span class="font-bold">1805</span>
                    – Seznam všech domů, ulic a náměstí královského hlavního a
                    rezidenčního města Prahy podle nového a starého číslování
                    (Verzeichniβ aller Häuser, Straβen und Plätze der
                    königlichen Haupt- und Residenzstadt Prag, nach der neuen
                    und alten Numerirung; mit Angabe der Hausbesitzer und
                    Schilde und einer vergleichenden Übersicht der alten und
                    neuen Numern, Praha 1805).
                </p>
                <p class="mb-5">
                    <span class="font-bold">1854</span>
                    – Rukopisný Soupis ulic a čísel domů (Verzeichniß der Gassen
                    und Haus Nummern), rozdělený podle tehdejších čtvrtí,
                    obsahuje ke každé ulici i čísla domů v ní se nacházejících a
                    české uliční názvy, které pravděpodobně nejsou vždy
                    autentické, jde spíše o překlad zde uvedených německých
                    názvů (Archiv hlavního města Prahy, MHMP I., Hlavní spisovna
                    – odd. B, manipulační období 1855–1864, sign. B 6/3).
                </p>

                <p class="mb-5">
                    <span class="font-bold">1860</span>
                    – Úřední seznamy ulic v němčině, vytvořené zvlášť pro Nové
                    Město, Staré Město, Josefov, Hradčany a Malou Stranu,
                    obsahují tehdejší názvy ulic, čísla popisná domů v nich se
                    nacházejících, návrhy na přejmenování některých ulic a
                    jejich odůvodnění, a nakonec definitivní rozhodnutí o
                    případné změně názvu též s odůvodněním (Archiv hlavního
                    města Prahy, MHMP I., Hlavní spisovna – odd. B, manipulační
                    období 1855–1864, sign. B 6/3).
                </p>

                <p class="mb-5">
                    <span class="font-bold">1870</span>
                    – Zásadní je Erbenův Autentický ukazatel (Karel Jaromír
                    Erben, Autentický ukazatel ulic a náměstí i čísel domovních
                    královského hlavního města Prahy, Praha 1870). V tištěné
                    verzi jsou názvy ulic česky, obsahuje staré a nově
                    ustanovené názvy ulic, čísla popisná domů v nich se
                    nacházejících a nově zavedená čísla uliční (modrá). V
                    Archivu hl. m. Prahy jsou uloženy Erbenovy rukopisné
                    podkladové seznamy, které obsahují také německé verze názvů
                    (Archiv hlavního města Prahy, Sbírka listin, sign. AMP PPL
                    III-9/33–AMP PPL III-9/37).
                </p>

                <p class="mb-5">
                    <span class="font-bold">1894</span>
                    – Usnesení a následná vyhláška městské rady z 18. dubna 1894
                    obsahující mimo jiné seznam ulic a pravidla jejich
                    označování (Archiv hl. m. Prahy, MHMP I., Protokoly sborů
                    městské správy, Protokol ze zasedání sboru obecních starších
                    18. 4. 1894, inv. č. 773, fol. 76r–77v., vyhláška byla
                    uveřejněna ve Věstníku obecním král. hlav. města Prahy 1894,
                    č. 7, s. 91–92).
                </p>

                <p class="font-bold underline">
                    20. století – hromadné změny a první historické přehledy
                </p>
                <p class="mb-5">
                    <span class="font-bold">1940</span>
                    – Úřední seznam ulic, náměstí a sadů hlavního města Prahy
                    (Úřední seznam ulic, náměstí a sadů hlavního města Prahy;
                    Amtliches Verzeichnis der Straßen, Plätze und Freiungen der
                    Hauptstadt Prag, Praha 1940). V něm jsou uvedeny české a
                    německé úřední názvy ulic po rozsáhlém přejmenování z roku
                    1940. Obsahuje pravděpodobně více názvů, než reálně
                    existovalo – jednak zahrnuje ulice naplánované koncem
                    třicátých let 20. století a ještě nevzniklé, jednak mnohé
                    názvy rozděloval na rozhraní katastrálních území na dva
                    různé.
                </p>

                <p class="mb-5">
                    <span class="font-bold">1958</span>
                    – Publikace
                    <span class="italic">Ulicemi města Prahy</span>
                    je prvním dílem, které se kromě samotných názvů ulic zabývá
                    celou jejich historií (Jiří Čarek – Václav Hlavsa – Josef
                    Janáček – Václav Lím, Ulicemi města Prahy od 14. století do
                    dneška, Praha 1958).
                </p>

                <p class="mb-5">
                    <span class="font-bold">1968, 1974</span>
                    – Seznamy ulic porovnávající staré a nové názvy v
                    připojených obcích k hl. m. Praze (nikoli v celé Praze) z
                    let 1968 a 1974 vznikly někdy koncem sedmdesátých let 20.
                    století jako výsledek mnohaleté práce odboru vnitřních věcí
                    NVP a místních národních výborů tehdy jednotlivých
                    připojených obcí. Seznamy neuvádějí původ ani odůvodnění
                    názvů (Archiv hl. m. Prahy, MHMP II, Odbor vnitřních věcí,
                    nesign.).
                </p>

                <p class="mb-5">
                    <span class="font-bold"
                        >60. léta 20. století – 2009/2010</span
                    >
                    – Plány vydané nakladatelstvím Kartografie obsahují
                    rejstříky ulic, které pro dobu svého vzniku představují
                    nejucelenější přehled aktuálních názvů pražských veřejných
                    prostranství. Nakladatelství spolupracovalo s městem a
                    komunikovalo i s názvoslovnou komisí, a rejstříky tudíž
                    odrážely nejspolehlivěji aktuální stav pražských ulic.
                </p>

                <p class="mb-5">
                    <span class="font-bold">1997/1998, 2012</span>
                    – První vydání Pražského uličníku, (Marek Lašťovka – Václav
                    Ledvinka et al., Pražský uličník, encyklopedie názvů
                    pražských veřejných prostranství, 1. a 2. díl, Praha 1997,
                    1998) se pokusilo podat co nejúplnější seznam pražských ulic
                    včetně starších názvů a jejich historie a problematických
                    názvů, které nevznikly či již zanikly. V toce 2012 byl
                    doplněn třetím svazkem (Kateřina Jíšová – Marek Lašťovka –
                    Barbora Lašťovková – Josef Třikač, Pražský uličník,
                    encyklopedie názvů pražských veřejných prostranství, 3. díl,
                    Praha 2012).
                </p>

                <p class="mb-5">
                    <span class="font-bold">2009/2010</span>
                    – podle zákona č. 111/2009 o základních registrech vznikl i
                    Registr územní identifikace, adres a nemovitostí (RÚIAN),
                    který eviduje názvy (adresy) v celé České republice a je i
                    pro Prahu úředním seznamem uličních názvů. V tomto směru
                    přinesl zásadní průlom v evidenci názvů pražských veřejných
                    prostranství, i když ne vždy koresponduje se schválenými
                    usneseními rady hlavního města Prahy. Eviduje aktuální stav
                    a nelze jej tedy použít pro „historický stav“ v určité době.
                </p>

                <p class="mb-10 sm:mb-25">
                    <span class="font-bold">2022</span>
                    – Druhé vydání pražského uličníku (Barbora Lašťovková –
                    Marek Lašťovka – Kateřina Jíšová – Josef Třikač – Václav
                    Ledvinka, Pražský uličník, encyklopedie názvů pražských
                    veřejných prostranství, Praha 2022).
                </p>

                <!-- next chapters -->
                <div class="mb-10 flex justify-between border-t-1 pt-5">
                    <p class="mb-5 w-1/2 border-r-1 font-bold">
                        <!-- nothing -->
                    </p>
                    <p class="mb-5 w-1/2 text-right font-bold">
                        <nuxt-link
                            to="/clanky/prameny-plany-prahy"
                            class="flex justify-end underline"
                        >
                            <span class="order-1 ml-2"
                                >Prameny – plány Prahy</span
                            >
                            <img
                                src="../../public/img/right-arrow.png"
                                alt=""
                                class="order-2 ml-2 inline h-5 w-5"
                            />
                        </nuxt-link>
                    </p>
                </div>
            </div>
            <div class="hidden md:col-span-1 md:block" />
        </div>

        <AppFooter />
    </div>
</template>

<script setup lang="ts"></script>

<style scoped></style>
